/* eslint-disable jsx-a11y/aria-role */
import React, { useEffect, useReducer, useState } from 'react';
import { Button, Icon, Spinner } from '@iwoca/orion';
import { Trans, t } from '@lingui/macro';
import clsx from 'clsx';
import { AddNewApplicationNavItem } from 'components/addNewApplication/AddNewApplicationNavItem';
import { Logo } from 'components/Logo/Logo';
import { Notifications } from 'components/Notifications/Notifications';
import { StagingBanner } from 'components/StagingBanner/StagingBanner';
import { useLocale } from 'hooks/useLocale';
import useWindowDimensions from 'hooks/useWindowDimensions';
import {
  getAvailableProducts,
  useQueryContext,
  useQueryIntroducer,
} from 'lendingApi/lendingApiHooks';
import { getEnvironment } from 'utils/environmentHelper/environmentHelper';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { BREAK_M, BREAK_XS } from '../../utils/breakpoints';
import { DropdownContainer } from './DropdownContainer/DropdownContainer';
import { HelpModal } from './Help/HelpModal';
import { InviteModal } from './Invite/InviteModal';
import {
  ACTIONS,
  dropdownReducer,
  initialReducerState,
} from './Navigation.helpers';
import styles from './Navigation.module.css';
import { NavItemLink } from './NavItemLink/NavItemLink';
import { SupportItems } from './Support/SupportItems';
import {
  handleAccountTracking,
  handleCalculatorClickTracking,
  handleDashboardClickTracking,
  handleInviteTracking,
  handleLogoutTracking,
  handleMobileMenuTracking,
  handleProductsClickTracking,
} from './Tracking/NavigationTracking';
import { WhatsNewButton } from './WhatsNewButton/WhatsNewButton';

export const Navigation = () => {
  const { locale, isDE } = useLocale();
  const [state, dispatch] = useReducer(dropdownReducer, initialReducerState);
  const [shouldShowInviteModal, setShouldShowInviteModal] = useState(false);
  const logoutLink = isDE ? '/partner/logout/' : '/introducer/logout';
  const [showHelpModal, setShowHelpModal] = useState(false);
  const { isLoading: contextLoading } = useQueryContext();

  const { data: introducer, isLoading: introducerLoading } =
    useQueryIntroducer();

  const firstName = introducer?.data?.introducer?.first_name;
  const lastName = introducer?.data?.introducer?.last_name;

  const handleAccountDropdown = () => {
    dispatch({ type: ACTIONS.ACCOUNT });
  };

  const handleMenuDropdown = () => {
    dispatch({ type: ACTIONS.MOBILE_MENU });
  };

  const handleCloseDropdown = () => {
    dispatch({ type: ACTIONS.CLOSE });
  };

  const { width } = useWindowDimensions();
  const showDesktopMenu = width >= BREAK_M;

  useEffect(() => {
    handleCloseDropdown();
  }, [showDesktopMenu]);

  const isShowingAccountDropdown =
    state.isDropdownOpen && state.selectedDropdown !== ACTIONS.NOTIFICATION;

  const noProductsUnlocked = getAvailableProducts(introducer).length === 0;

  const toggleMobileMenu = () => {
    if (isShowingAccountDropdown) {
      handleCloseDropdown();
    } else {
      handleMobileMenuTracking(locale);
      handleMenuDropdown();
    }
  };

  const renderNavItems = () => {
    return (
      <>
        <NavItemLink
          href={PATH_NAMES.home()}
          label={t`Dashboard`}
          onClick={() => handleDashboardClickTracking(locale)}
          disabled={noProductsUnlocked}
        />
        <NavItemLink
          href={PATH_NAMES.products()}
          label={t`Products`}
          onClick={() => handleProductsClickTracking(locale)}
        />
        <NavItemLink
          href={PATH_NAMES.calculator()}
          label={t`Calculator`}
          onClick={() => handleCalculatorClickTracking(locale)}
          disabled={noProductsUnlocked}
        />
      </>
    );
  };

  const renderAccountButtonContent = () => {
    if (contextLoading || introducerLoading) {
      return <Spinner />;
    }

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`;
    }

    return <Icon icon="userFilled" data-testid="account-icon-fallback" />;
  };

  const renderAccountIcon = () => {
    return (
      <Button
        role="account"
        className={styles.accountIcon}
        variant="denim"
        onClick={() => {
          if (showDesktopMenu) {
            handleAccountTracking(locale);
            handleAccountDropdown();
          }
        }}
        size="large"
        data-testid="account-icon-button"
      >
        {renderAccountButtonContent()}
      </Button>
    );
  };

  const renderAccountDetail = () => {
    if (!firstName && !lastName) {
      return null;
    }

    return (
      <div className={clsx(styles.navItem, styles.navAccountDetail)}>
        {!showDesktopMenu && renderAccountIcon()}
        <span className={styles.navItemLink}>
          {firstName} {lastName}
        </span>
      </div>
    );
  };

  const renderAccountDropdownContent = () => {
    return (
      <>
        <SupportItems setShowHelpModal={setShowHelpModal} />
        <div className={styles.navItem}>
          <button
            className={styles.navItemLink}
            onClick={() => {
              handleInviteTracking(locale);
              setShouldShowInviteModal(true);
            }}
          >
            <Trans>Invite</Trans>
          </button>
        </div>
        <div className={styles.navItem}>
          <a
            href={logoutLink}
            className={clsx(styles.navItemLink, styles.logoutLink)}
            onClick={() => handleLogoutTracking(locale, logoutLink)}
          >
            <Trans>Logout</Trans>
          </a>
        </div>
      </>
    );
  };

  const renderMobileMenu = () => {
    return (
      <>
        {renderAccountDetail()}
        <WhatsNewButton />
        {renderNavItems()}
        {renderAccountDropdownContent()}
      </>
    );
  };

  const renderAccountDropdownSection = () => {
    return (
      <DropdownContainer
        onClose={handleCloseDropdown}
        className={clsx(styles.navDropdown, {
          [styles.accountDropdown]: state.selectedDropdown === ACTIONS.ACCOUNT,
          [styles.mobileMenuDropdown]:
            state.selectedDropdown === ACTIONS.MOBILE_MENU,
        })}
      >
        {state.selectedDropdown === ACTIONS.ACCOUNT && (
          <>
            {renderAccountDetail()}
            {renderAccountDropdownContent()}
          </>
        )}
        {state.selectedDropdown === ACTIONS.MOBILE_MENU && renderMobileMenu()}
      </DropdownContainer>
    );
  };

  const renderDesktopAccountItems = () => {
    return (
      <div className={clsx(styles.accountItem, styles.accountIconWrapper)}>
        {renderAccountIcon()}
      </div>
    );
  };

  const renderMobileMenuButton = () => {
    return (
      <div className={styles.accountItem}>
        <Button
          variant="tertiary"
          className={clsx(styles.navButton, styles.mobileMenuButton)}
          data-testid="menu-button"
          onClick={toggleMobileMenu}
        >
          <div
            className={clsx(styles.hamburgerIconWrapper, {
              [styles.isOpen]: isShowingAccountDropdown,
            })}
          >
            <Icon
              icon={isShowingAccountDropdown ? 'timesDefault' : 'hamburgerMenu'}
              className={styles.hamburgerIcon}
            />
          </div>
        </Button>
      </div>
    );
  };

  const renderModals = () => {
    return (
      <>
        <InviteModal
          showModal={shouldShowInviteModal}
          closeModal={() => setShouldShowInviteModal(false)}
        />
        <HelpModal
          showModal={showHelpModal}
          closeModal={() => setShowHelpModal(false)}
        />
      </>
    );
  };

  return (
    <div
      className={clsx(styles.navigationWrapper, {
        [styles.isStaging]: getEnvironment() === 'staging',
      })}
    >
      <StagingBanner />
      <nav className={styles.navigation} data-testid="navigation">
        <div className={styles.navItems}>
          <Logo iconOnly={width <= BREAK_XS} />
          {showDesktopMenu && renderNavItems()}

          <div className={styles.navItem}>
            <AddNewApplicationNavItem showDesktopVersion={width >= BREAK_XS} />
          </div>
        </div>

        <div className={styles.accountItems}>
          {showDesktopMenu && (
            <div className={styles.accountItem}>
              <WhatsNewButton disabled={noProductsUnlocked} />
            </div>
          )}

          <div className={styles.accountItem}>
            <Notifications />
          </div>

          {showDesktopMenu && renderDesktopAccountItems()}
          {!showDesktopMenu && renderMobileMenuButton()}
        </div>
      </nav>

      {/* Modals and dropdowns */}
      {isShowingAccountDropdown && renderAccountDropdownSection()}
      {renderModals()}
    </div>
  );
};
